export const SOCIAL_MAX = 5;
export const SOCIAL_URL_MAX = 1024;

export type SocialPlatform =
  | 'youtube'
  | 'x'
  | 'instagram'
  | 'facebook'
  | 'github'
  | 'twitch'
  | 'linkedin'
  | 'tiktok'
  | 'discord'
  | 'bilibili'
  | 'weibo'
  | 'steam'
  | 'reddit'
  | 'telegram'
  | 'spotify'
  | 'soundcloud'
  | 'patreon'
  | 'threads'
  | 'douyin'
  | 'xiaohongshu'
  | 'vimeo'
  | 'pinterest'
  | 'web';

const HOST_RULES: Array<[RegExp, SocialPlatform]> = [
  [/(^|\.)youtube\.com$/, 'youtube'],
  [/(^|\.)youtu\.be$/, 'youtube'],
  [/(^|\.)twitter\.com$/, 'x'],
  [/(^|\.)x\.com$/, 'x'],
  [/(^|\.)instagram\.com$/, 'instagram'],
  [/(^|\.)facebook\.com$/, 'facebook'],
  [/(^|\.)fb\.com$/, 'facebook'],
  [/(^|\.)fb\.me$/, 'facebook'],
  [/(^|\.)github\.com$/, 'github'],
  [/(^|\.)twitch\.tv$/, 'twitch'],
  [/(^|\.)linkedin\.com$/, 'linkedin'],
  [/(^|\.)tiktok\.com$/, 'tiktok'],
  [/(^|\.)discord\.com$/, 'discord'],
  [/(^|\.)discord\.gg$/, 'discord'],
  [/(^|\.)bilibili\.com$/, 'bilibili'],
  [/(^|\.)b23\.tv$/, 'bilibili'],
  [/(^|\.)weibo\.com$/, 'weibo'],
  [/(^|\.)steamcommunity\.com$/, 'steam'],
  [/^(store\.)?steampowered\.com$/, 'steam'],
  [/(^|\.)reddit\.com$/, 'reddit'],
  [/(^|\.)redd\.it$/, 'reddit'],
  [/(^|\.)t\.me$/, 'telegram'],
  [/(^|\.)telegram\.org$/, 'telegram'],
  [/(^|\.)telegram\.me$/, 'telegram'],
  [/(^|\.)spotify\.com$/, 'spotify'],
  [/(^|\.)soundcloud\.com$/, 'soundcloud'],
  [/(^|\.)patreon\.com$/, 'patreon'],
  [/(^|\.)threads\.net$/, 'threads'],
  [/(^|\.)douyin\.com$/, 'douyin'],
  [/(^|\.)xiaohongshu\.com$/, 'xiaohongshu'],
  [/(^|\.)xhslink\.com$/, 'xiaohongshu'],
  [/(^|\.)vimeo\.com$/, 'vimeo'],
  [/(^|\.)pinterest\.com$/, 'pinterest'],
  [/(^|\.)pin\.it$/, 'pinterest'],
];

export function hostOf(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const withScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed) ? trimmed : `https://${trimmed}`;
    const url = new URL(withScheme);
    return url.hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return null;
  }
}

export function detectSocialPlatform(raw: string): SocialPlatform {
  const host = hostOf(raw);
  if (!host) return 'web';
  for (const [pattern, platform] of HOST_RULES) {
    if (pattern.test(host)) return platform;
  }
  return 'web';
}

export function isValidSocialUrl(raw: string): boolean {
  return normalizeSocialUrl(raw) !== null;
}

/** Empty string → null. Invalid → false. Valid http(s) URL → normalized string. */
export function normalizeSocialUrl(raw: string): string | null | false {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.length > SOCIAL_URL_MAX) return false;
  if (/[\u0000-\u001F\u007F]/.test(trimmed)) return false;
  const lower = trimmed.toLowerCase();
  if (
    lower.startsWith('javascript:') ||
    lower.startsWith('data:') ||
    lower.startsWith('vbscript:') ||
    lower.startsWith('file:') ||
    lower.startsWith('blob:')
  ) {
    return false;
  }
  const withScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed) ? trimmed : `https://${trimmed}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return false;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
  if (!url.hostname) return false;
  if (url.username || url.password) return false;
  const path = url.pathname === '/' ? '' : url.pathname;
  return `${url.protocol}//${url.host}${path}${url.search}${url.hash}`;
}

export function normalizeSocialList(urls: readonly string[]): string[] {
  const out: string[] = [];
  for (const raw of urls) {
    const next = normalizeSocialUrl(raw);
    if (next === null || next === false) continue;
    if (!out.includes(next)) out.push(next);
    if (out.length >= SOCIAL_MAX) break;
  }
  return out;
}

export function parseSocialLinks(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item === 'string' && item.trim()) out.push(item.trim());
    else if (item && typeof item === 'object' && 'url' in item && typeof (item as { url: unknown }).url === 'string') {
      const url = (item as { url: string }).url.trim();
      if (url) out.push(url);
    }
  }
  return out;
}
